import * as S from './mobileStyled'

import React, { useState } from 'react'
import {
  getAdjustmentValue,
  getSelectedTee,
  refreshHole,
} from '@app/evaluation-core'

import EvalTabItem from '../../atoms/EvalTabItem'
import { LockStatus, TeamRole } from '@app/evaluation-core/types'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import TwoButtonModal from '../../organisms/TwoButtonModal'
import { setSelectedHole } from '@app/evaluation-core/actions/evaluationActions'
import theme from '@app/ui/theme'
import useAdjustment from '@common/hooks/useAdjustment'
import { useDispatch } from 'react-redux'
import { useEvaluation } from '@common/hooks'
import useEvaluationLockedState from '@common/hooks/useEvaluationLockedState'
import useEvaluationUser from '@common/hooks/useEvaluationUser'
import { useSnackbar } from '@app/snackbar'
import useReadOnly from '@app/evaluation/hooks/useReadOnly'
import DateTimeView from '../../atoms/DateTimeView'
import { EvalStatus } from '@app/courses/domain/ratingStatus'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import EvaluationSideBarHeader from '../EvaluationSideBarHeader/EvaluationSideBarHeader'

interface SideBarProps {
  collapsedMode: any
  setCollapsedMode: any
  holeDecrementState: () => void
  holeIncrementState: () => void
  dropMenuRef: any
  openModal: () => void
  setHoleLocked: () => void
  lockAllHoles: () => void
  reference?: React.RefObject<HTMLDivElement>
}

const MobileEvaluationSideBar = ({
  collapsedMode,
  setCollapsedMode,
  holeDecrementState,
  holeIncrementState,
  dropMenuRef,
  openModal,
  setHoleLocked,
  lockAllHoles,
  reference,
}: SideBarProps) => {
  const [openSnackbar] = useSnackbar()
  const evaluationState = useEvaluation()
  const { currentId, evaluation } = evaluationState
  const { view, isAdmin } = useEvaluationUser()
  const adjustments = useAdjustment()
  const dispatch = useDispatch<any>()
  const lockStatus = useEvaluationLockedState()
  const tee = getSelectedTee(evaluation)
  const [showRefreshModal, setShowRefreshModal] = useState(false)
  const handleReadOnly = useReadOnly()
  const isPhone = window.innerWidth <= 767

  const getTeamLeaderEmail = () => {
    const teamleader = Object.values(evaluation.users).find(
      (user) => user.role === TeamRole.LEADER
    )
    return teamleader?.view?.email
  }

  const handleLockButtonClick = () => {
    if (isAdmin) {
      handleReadOnly(setHoleLocked)()
    } else {
      const teamleader = getTeamLeaderEmail()
      if (teamleader) {
        openSnackbar(
          `Only the Team Leader (${teamleader}) can lock/unlock holes`
        )
      } else {
        openSnackbar('Only the Team Leader can lock/unlock holes')
      }
    }
  }

  const handleLockAllButtonClick = () => {
    if (isAdmin) {
      lockAllHoles()
    } else {
      const teamleader = getTeamLeaderEmail()
      if (teamleader) {
        openSnackbar(
          `Only the Team Leader (${teamleader}) can lock/unlock holes`
        )
      } else {
        openSnackbar('Only the Team Leader can lock/unlock holes')
      }
    }
  }

  const handleRefresh = () => {
    refreshHole(view?.hole, currentId as string)
    setModal(false)()
  }

  const setModal = (visible: boolean) => () => setShowRefreshModal(visible)

  const allHolesIsLocked =
    evaluation?.holes.length > 0 &&
    evaluation?.holes?.filter((x) => x.locked).length ==
      evaluation?.holes.length
  const holeIsLocked = lockStatus === LockStatus.LOCKED
  const isDev = process.env.NODE_ENV === 'development'
  const hasLeaderRights = isAdmin
  const getLockHoleText = () => {
    const collapsedText = holeIsLocked ? 'Unlock' : 'Lock'
    const expandedText = holeIsLocked ? 'Unlock Hole' : 'Lock Hole'

    return collapsedMode ? collapsedText : expandedText
  }

  const getLockAllText = () => {
    const collapsedText = allHolesIsLocked ? 'Unlock all' : 'Lock all'
    const expandedText = allHolesIsLocked ? 'Unlock all' : 'Lock all'

    return collapsedMode ? collapsedText : expandedText
  }

  const branchRevision = getAdjustmentValue(
    'hole',
    view,
    'BranchRevision',
    adjustments
  )

  const crDate = getAdjustmentValue(
    'hole',
    view,
    'CRDate',
    adjustments
  ) as string

  const convertUTCToLocal = (dateString: string, dateOnly?: boolean) => {
    const date = new Date(dateString + 'Z')
    if (dateOnly) {
      return date.toLocaleDateString()
    }
    const formattedDate = date.toLocaleString('sv-SE')
    return formattedDate
  }

  const getCrDate = () => {
    return convertUTCToLocal(crDate)
  }

  const crVersion = getAdjustmentValue('hole', view, 'CRVersion', adjustments)

  return (
    <div style={{ flexDirection: 'column', width: '50%' }}>
      <EvaluationSideBarHeader
        collapsedMode={collapsedMode}
        holeDecrementState={holeDecrementState}
        holeIncrementState={holeIncrementState}
        dropMenuRef={dropMenuRef}
      />

      <S.EvalSideBar
        collapsed={collapsedMode}
        ref={reference}
        inAdjustment={view?.adjustment !== -1}
      >
        <S.EvalSideBarMain inAdjustment={view?.adjustment !== -1}>
          {view.adjustment === -1 && (
            <>
              <S.EvalSideBarGroupRow>
                <a>
                  {collapsedMode ? 'EPL' : 'Eff. Playing Length Corrections'}
                </a>
              </S.EvalSideBarGroupRow>
              {evaluation.adjustments.slice(0, 3).map((item, index: number) => {
                const usersOnAdjustment = Object.values(
                  evaluation.users
                ).filter(
                  (user) =>
                    user.view?.hole === view?.hole &&
                    item.adjustmentIndex === user.view?.adjustment
                )

                return (
                  <EvalTabItem
                    key={index}
                    item={
                      collapsedMode ? item.shortName.toUpperCase() : item.name
                    }
                    active={
                      item.adjustmentIndex === view?.adjustment ||
                      (item.adjustmentIndex === view?.prevAdj &&
                        view.adjustment === -1)
                    }
                    onPress={() =>
                      dispatch(
                        setSelectedHole(
                          view?.hole,
                          view?.tee,
                          view?.golfer,
                          item.adjustmentIndex
                        )
                      )
                    }
                    activeUsers={usersOnAdjustment}
                    sideBarCollapsed={collapsedMode}
                  />
                )
              })}
              <S.EvalSideBarGroupRow>
                <a>{collapsedMode ? 'ORF' : 'Obstacle Rating Factors'}</a>
              </S.EvalSideBarGroupRow>
              {evaluation.adjustments.slice(3).map((item, index: number) => {
                const usersOnAdjustment = Object.values(
                  evaluation.users
                ).filter(
                  (user) =>
                    user.view?.hole === view?.hole &&
                    item.adjustmentIndex === user.view?.adjustment
                )
                return (
                  <EvalTabItem
                    key={index}
                    item={
                      collapsedMode ? item.shortName.toUpperCase() : item.name
                    }
                    active={
                      item.adjustmentIndex === view?.adjustment ||
                      (item.adjustmentIndex === view?.prevAdj &&
                        view.adjustment === -1)
                    }
                    onPress={() =>
                      dispatch(
                        setSelectedHole(
                          view?.hole,
                          view?.tee,
                          view?.golfer,
                          item.adjustmentIndex
                        )
                      )
                    }
                    activeUsers={usersOnAdjustment}
                    sideBarCollapsed={collapsedMode}
                  />
                )
              })}
            </>
          )}
          {view.adjustment !== -1 && (
            <>
              <S.EvalSideBarGroupRow>
                <a>
                  {collapsedMode ? 'EPL' : 'Eff. Playing Length Corrections'}
                </a>
              </S.EvalSideBarGroupRow>
              {evaluation.adjustments.slice(0, 3).map((item, index: number) => {
                const usersOnAdjustment = Object.values(
                  evaluation.users
                ).filter(
                  (user) =>
                    user.view?.hole === view?.hole &&
                    item.adjustmentIndex === user.view?.adjustment
                )

                return (
                  <EvalTabItem
                    key={index}
                    item={
                      collapsedMode ? item.shortName.toUpperCase() : item.name
                    }
                    active={
                      item.adjustmentIndex === view?.adjustment ||
                      (item.adjustmentIndex === view?.prevAdj &&
                        view.adjustment === -1)
                    }
                    onPress={() =>
                      dispatch(
                        setSelectedHole(
                          view?.hole,
                          view?.tee,
                          view?.golfer,
                          item.adjustmentIndex
                        )
                      )
                    }
                    activeUsers={usersOnAdjustment}
                    sideBarCollapsed={collapsedMode}
                  />
                )
              })}
              <S.EvalSideBarGroupRow>
                <a>{collapsedMode ? 'ORF' : 'Obstacle Rating Factors'}</a>
              </S.EvalSideBarGroupRow>
              {evaluation.adjustments.slice(3).map((item, index: number) => {
                const usersOnAdjustment = Object.values(
                  evaluation.users
                ).filter(
                  (user) =>
                    user.view?.hole === view?.hole &&
                    item.adjustmentIndex === user.view?.adjustment
                )
                return (
                  <EvalTabItem
                    key={index}
                    item={
                      collapsedMode ? item.shortName.toUpperCase() : item.name
                    }
                    active={
                      item.adjustmentIndex === view?.adjustment ||
                      (item.adjustmentIndex === view?.prevAdj &&
                        view.adjustment === -1)
                    }
                    onPress={() =>
                      dispatch(
                        setSelectedHole(
                          view?.hole,
                          view?.tee,
                          view?.golfer,
                          item.adjustmentIndex
                        )
                      )
                    }
                    activeUsers={usersOnAdjustment}
                    sideBarCollapsed={collapsedMode}
                  />
                )
              })}
            </>
          )}
        </S.EvalSideBarMain>
        <TwoButtonModal
          isVisible={showRefreshModal}
          title="Are you sure you want to refresh?"
          description={`This will overwrite the current evaluation with the latest information from LiveCR for Hole: ${
            view?.hole + 1
          }`}
          onPressLeft={handleRefresh}
          onPressRight={setModal(false)}
          leftButtonText="Refresh"
          rightButtonText="Cancel"
        />
        {isDev && false && (
          <div style={{ padding: !isPhone ? 8 : 4 }}>
            <span>Eval ID</span>&nbsp;
            <div
              style={{
                padding: !isPhone ? 4 : 2,
                backgroundColor: theme.colors.light,
              }}
            >
              {currentId}
            </div>
          </div>
        )}
        {view?.adjustment !== -1 && (
          <S.LiveCRInfoContainer>
            <div style={{ margin: !isPhone ? 4 : 2, width: '80%' }}>
              <RoundedButton
                title={getLockHoleText()}
                onPress={handleLockButtonClick}
                disabled={lockStatus === LockStatus.READ_ONLY}
              />
            </div>
            <div style={{ margin: !isPhone ? 4 : 2, width: '80%' }}>
              <RoundedButton
                title={getLockAllText()}
                onPress={handleLockAllButtonClick}
                disabled={evaluation.status == EvalStatus.FINALIZED}
              />
            </div>
            <S.LiveCRInfoTextContainer>
              {branchRevision === 0 ? (
                <Skeleton height={!isPhone ? 20 : 10} width={'90%'} />
              ) : (
                <S.LiveCRInfoText>
                  {collapsedMode ? `Rev: ` : `Revision: `}
                  <div
                    style={{
                      display: 'inline',
                      fontWeight: !isPhone ? 600 : 300,
                    }}
                  >
                    {branchRevision}
                  </div>
                </S.LiveCRInfoText>
              )}
              {crVersion === 0 ? (
                <Skeleton height={!isPhone ? 20 : 10} width={'90%'} />
              ) : (
                <S.LiveCRInfoText>
                  {collapsedMode ? `CR v. ` : `LiveCR Version: `}
                  <div
                    style={{
                      display: 'inline',
                      fontWeight: !isPhone ? 600 : 300,
                    }}
                  >
                    {crVersion}
                  </div>
                </S.LiveCRInfoText>
              )}
              <S.LiveCRInfoText>
                {!collapsedMode && 'Last synced: '}
                <DateTimeView dateString={getCrDate()} splitRows />
              </S.LiveCRInfoText>
            </S.LiveCRInfoTextContainer>
          </S.LiveCRInfoContainer>
        )}
      </S.EvalSideBar>
    </div>
  )
}

export default MobileEvaluationSideBar
